import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Download, ExternalLink, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/PageHeader';
import api, { API } from '@/lib/api';
import { downloadAuthedFile } from '@/lib/download';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const STATUS_BADGE = {
    draft: 'bg-slate-50 text-slate-700 border-slate-200',
    finalized: 'bg-sky-50 text-sky-700 border-sky-200',
    sent: 'bg-amber-50 text-amber-800 border-amber-200',
    viewed: 'bg-violet-50 text-violet-700 border-violet-200',
    signed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    declined: 'bg-red-50 text-red-700 border-red-200',
};

export default function QuotesListPage() {
    const [tab, setTab] = useState('all');
    const [quotes, setQuotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = () => {
        setLoading(true);
        const params = tab === 'all' ? {} : { status: tab };
        api.get('/quotes', { params }).then((r) => setQuotes(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
    };

    useEffect(load, [tab]);

    const createBlank = async () => {
        try {
            const r = await api.post('/quotes', {});
            navigate(`/app/quotes/${r.data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to create quote');
        }
    };

    return (
        <>
            <PageHeader
                title="Quotes"
                subtitle="Drafts, sent quotes, and signed contracts — all in one place."
                testId="quotes-header"
                actions={
                    <Button onClick={createBlank} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="quote-new-blank-button">
                        <Plus className="h-4 w-4 mr-1" /> New quote
                    </Button>
                }
            />
            <Tabs value={tab} onValueChange={setTab} className="mb-4">
                <TabsList>
                    {['all','draft','finalized','sent','viewed','signed','declined'].map((s) => (
                        <TabsTrigger key={s} value={s} data-testid={`quotes-tab-${s}`}>{s}</TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-6 text-slate-500">Loading…</div>
                    ) : quotes.length === 0 ? (
                        <div className="p-10 text-center text-slate-500">No quotes here. <button onClick={createBlank} className="text-slate-900 underline">Create one</button>.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                                <tr className="text-left text-slate-700">
                                    <th className="py-3 px-4">Quote #</th>
                                    <th className="py-3 px-4">Homeowner</th>
                                    <th className="py-3 px-4">Address</th>
                                    <th className="py-3 px-4">Status</th>
                                    <th className="py-3 px-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quotes.map((q) => (
                                    <tr key={q.id} className="border-t hover:bg-slate-50" data-testid={`quote-row-${q.id}`}>
                                        <td className="py-2 px-4 font-mono text-xs">{q.quote_number}</td>
                                        <td className="py-2 px-4">{q.homeowner_name || '—'}</td>
                                        <td className="py-2 px-4 text-slate-600">{q.property_address || '—'}</td>
                                        <td className="py-2 px-4"><Badge variant="outline" className={STATUS_BADGE[q.status] || ''}>{q.status}</Badge></td>
                                        <td className="py-2 px-4 text-right">
                                            <Button asChild variant="ghost" size="sm" data-testid={`quote-open-${q.id}`}><Link to={`/app/quotes/${q.id}`}>Open</Link></Button>
                                            <Button
                                                onClick={() => downloadAuthedFile(`/quotes/${q.id}/pdf`, `${q.quote_number || q.id}.pdf`)}
                                                variant="ghost"
                                                size="sm"
                                                data-testid={`quote-pdf-${q.id}`}
                                                title="Download PDF"
                                            >
                                                <Download className="h-3.5 w-3.5" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </>
    );
}
